import { proxyCustomElement, HTMLElement, createEvent, h, Fragment } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { S as StringService } from './index2.js';
import { D as DomService } from './index3.js';
import { D as DateService } from './index8.js';
import { T as TranslationService } from './index6.js';
import { T as TeaserLayout } from './roche-teaser.types.js';
import { F as FuzzinessOption } from './roche-search.types.js';
import { d as defineCustomElement$9 } from './roche-button2.js';
import { d as defineCustomElement$8 } from './roche-empty-state2.js';
import { d as defineCustomElement$7 } from './roche-icon2.js';
import { d as defineCustomElement$2, a as defineCustomElement$6 } from './roche-image2.js';
import { d as defineCustomElement$5 } from './roche-loading-spinner2.js';
import { d as defineCustomElement$4 } from './roche-pagination2.js';
import { d as defineCustomElement$3 } from './roche-teaser2.js';

const rocheListSearchCss = ":host{display:block}.list-search .input-container{z-index:150;position:relative;margin-top:2rem;width:100%}@media (min-width: 48rem){.list-search .input-container{width:66.6666666667%;margin-top:3rem}}.list-search .input-container input{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;font-family:\"roche-sans\";font-weight:200;background-color:transparent;border:none;border-bottom:1px solid #706b69;border-radius:0;padding:0.3125rem 1.875rem 0.75rem 2.5rem;width:100%}@media (min-width: 64rem){.list-search .input-container input{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}@media (min-width: 48rem){.list-search .input-container input{font-size:1.5625rem;line-height:2.1875rem}}@supports (-webkit-overflow-scrolling: touch){.list-search .input-container input{-webkit-appearance:none}}.list-search .input-container input:active,.list-search .input-container input:hover,.list-search .input-container input:focus{border-bottom-color:#0b41cd}.list-search .input-container input::placeholder{color:#706b69;margin-right:0.625rem}.list-search .input-container input:focus{outline:none}.list-search .input-container ::-webkit-search-decoration,.list-search .input-container ::-webkit-search-cancel-button,.list-search .input-container ::-webkit-search-results-button,.list-search .input-container ::-webkit-search-results-decoration{display:none}.list-search .input-container roche-icon{fill:#706b69;position:absolute;top:0.4375rem;left:0}@media (min-width: 48rem){.list-search .input-container roche-icon{top:0.6875rem}}.list-search .input-container .clear-input{position:absolute;top:0.1875rem;right:1.375rem;visibility:hidden}.list-search .input-container .clear-input:hover{cursor:pointer}.list-search .input-container .clear-input--hidden{visibility:visible}.list-search .load-more{display:none}@media (min-width: 20rem){.list-search .load-more roche-loading-spinner{margin-top:0}}@media (min-width: 48rem){.list-search .load-more roche-loading-spinner{margin-top:0}}.list-search .load-more--active{display:block}@media (min-width: 20rem){.list-search .load-more--active roche-loading-spinner{margin-top:8rem}}@media (min-width: 48rem){.list-search .load-more--active roche-loading-spinner{margin-top:10rem}}.list-search .empty-state{display:none}@media (min-width: 20rem){.list-search .empty-state roche-empty-state{margin-top:0}}@media (min-width: 48rem){.list-search .empty-state roche-empty-state{margin-top:0}}.list-search .empty-state--active{display:block}@media (min-width: 20rem){.list-search .empty-state--active roche-empty-state{margin-top:8rem}}@media (min-width: 48rem){.list-search .empty-state--active roche-empty-state{margin-top:10rem}}:host([dir=rtl]) .list-search .input-container input{padding:0.3125rem 2.5rem 0.75rem 1.875rem}:host([dir=rtl]) .list-search .input-container roche-icon{left:auto;right:0}:host([dir=rtl]) .list-search .input-container .clear-input{left:1.375rem;right:auto}";
const RocheListSearchStyle0 = rocheListSearchCss;

const RocheListSearch$1 = /*@__PURE__*/ proxyCustomElement(class RocheListSearch extends HTMLElement {
    triggerNewPage(event) {
        this.searchResults = [];
        const [paginationComponent] = event.composedPath();
        this.pageTracker = this.pageTracker
            .map((number, index) => (index === parseInt(paginationComponent === null || paginationComponent === void 0 ? void 0 : paginationComponent.dataset.tabIndex, 10)
            ? event.detail
            : number));
        this.querySearch();
    }
    get searchFilters() {
        return JSON.parse(this.filters);
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.changeEvent = createEvent(this, "rocheInputChange", 7);
        this.filters = '[{}]';
        this.inputValue = undefined;
        this.disableBuiltInCludoSearch = false;
        this.searchTerm = undefined;
        this.searchResults = [];
        this.isLoading = false;
        this.resultsPerPage = 6;
        this.pageTracker = [];
        this.translations = undefined;
        this.dates = undefined;
        /*
         * To prevent race conditions on search steps (trigger, url param read, url param update, etc)
         * Keep the same debounce times for all methods which call one or more of these steps.
         */
        const searchStepDebounceTime = 500;
        this.handleChange = debounce_1.debounce(this.handleChange.bind(this), searchStepDebounceTime, false);
        this.querySearch = debounce_1.debounce(this.querySearch.bind(this), searchStepDebounceTime, false);
        this.handleClearInput = this.handleClearInput.bind(this);
    }
    async componentWillLoad() {
        this.dates = new Map();
        this.translations = {
            inputPlaceholder: await TranslationService.translate('enter your search term'),
        };
    }
    componentDidLoad() {
        var _a;
        this.listToHide = (_a = this.element) === null || _a === void 0 ? void 0 : _a.nextElementSibling;
        DomService.handleTextDirection(this.element);
    }
    render() {
        var _a;
        const clearInputClasses = {
            'clear-input': true,
            'clear-input--hidden': (!!this.searchTerm || !!this.inputValue),
        };
        const loadMoreClasses = {
            'load-more': true,
            'load-more--active': this.isLoading,
        };
        return (h("div", { key: '667f01b82809b3323bb1450e3135a583c2e476d4', class: "list-search" }, h("div", { key: 'ebd3713467d7bc2aa4679e7139eb92b78d70a901', class: "input-container" }, h("roche-icon", { key: 'd40fa1d47e5fabb958c44fd0faf012f24753653b', icon: "search", height: "1.5rem" }), h("input", { key: 'c67d0f788e0611c7990c49e1e29f362ebb84cf51', class: "input", placeholder: StringService.capitalize(this.translations.inputPlaceholder), type: "search", ref: (el) => { this.inputElement = el; }, onInput: this.handleChange, value: this.inputValue }), h("span", { key: 'e15f82dd734a770c6b43d8f3e11d8ea2e3b2ae6b', class: clearInputClasses, onClick: this.handleClearInput }, h("roche-icon", { key: '2612e21bf6c459509926e41b25b73ad7d5870fb5', icon: "remove", height: "1.5rem" }))), !this.disableBuiltInCludoSearch && h(Fragment, null, h("div", { class: loadMoreClasses }, h("roche-loading-spinner", null, " ")), h("div", { class: "results-container" }, (_a = this.searchResults) === null || _a === void 0 ? void 0 : _a.map((result, index) => {
            var _a, _b, _c;
            const totalPages = Math.ceil(result.TotalDocument / this.resultsPerPage);
            if (((_a = result.TypedDocuments) === null || _a === void 0 ? void 0 : _a.length) <= 0 && ((_b = this.searchTerm) === null || _b === void 0 ? void 0 : _b.length) >= 1) {
                return (h("roche-empty-state", { key: index }));
            }
            return (h("div", { class: "teaser-container", key: index }, (_c = result.TypedDocuments) === null || _c === void 0 ? void 0 :
                _c.map((document, i) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    return (h("roche-teaser", { key: i, layout: TeaserLayout.VARIANT_C_WITHOUT_IMAGE, teaserTitle: (_b = (_a = document.Fields) === null || _a === void 0 ? void 0 : _a.Title) === null || _b === void 0 ? void 0 : _b.Value, teaserOverline: (_c = this.dates) === null || _c === void 0 ? void 0 : _c.get((_e = (_d = document.Fields) === null || _d === void 0 ? void 0 : _d.Date) === null || _e === void 0 ? void 0 : _e.Value), teaserUrl: (_g = (_f = document.Fields) === null || _f === void 0 ? void 0 : _f.Url) === null || _g === void 0 ? void 0 : _g.Value }));
                }), h("roche-pagination", { activePage: this.pageTracker[index], totalPages: totalPages, style: { display: totalPages > 1 ? 'block' : 'none' }, "data-tab-index": index, "data-rpp": this.resultsPerPage })));
        })))));
    }
    buildSearchQueries() {
        const { 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        cludoCustomerId, cludoEngineId, locale, websiteName, cludoFuzziness, } = DomService.getGlobalConfig();
        // Use cludo fuzzyness only if there are special characters in the query term
        // eslint-disable-next-line no-control-regex
        const hasSpecialCharacters = /[^\x00-\x7F]/.test(this.searchTerm);
        let fuzziness;
        if ((cludoFuzziness === null || cludoFuzziness === void 0 ? void 0 : cludoFuzziness.toUpperCase()) === FuzzinessOption.ON) {
            fuzziness = true;
        }
        else if ((cludoFuzziness === null || cludoFuzziness === void 0 ? void 0 : cludoFuzziness.toUpperCase()) === FuzzinessOption.OFF) {
            fuzziness = false;
        }
        else {
            fuzziness = hasSpecialCharacters;
        }
        return this.searchFilters
            .map((filter, index) => () => {
            const mergedFilters = Object.assign(Object.assign({}, filter), { WebsiteName: [websiteName], Lang: [locale] });
            return fetch(`https://api.cludo.com/api/v3/${cludoCustomerId}/${cludoEngineId}/search`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `SiteKey ${btoa(`${cludoCustomerId}:${cludoEngineId}:SiteKey`)}`,
                },
                body: JSON.stringify({
                    query: fuzziness ? `${this.searchTerm}` : `${this.searchTerm}~`,
                    perPage: this.resultsPerPage,
                    page: this.pageTracker[index],
                    enableFacetFiltering: true,
                    filters: mergedFilters,
                    sort: {
                        Date: 'desc',
                    },
                }),
            })
                .then((response) => response.json())
                // eslint-disable-next-line no-console
                .catch((error) => { console.log(error); });
        });
    }
    async querySearch() {
        var _a;
        this.isLoading = true;
        if (this.disableBuiltInCludoSearch) {
            return;
        }
        this.searchResults = await Promise
            .all(this.buildSearchQueries().map((request) => request()));
        this.searchResults.forEach(async (result) => {
            var _a;
            (_a = result.TypedDocuments) === null || _a === void 0 ? void 0 : _a.map(async (document) => {
                var _a, _b, _c, _d;
                const dateValue = (_b = (_a = document.Fields) === null || _a === void 0 ? void 0 : _a.Date) === null || _b === void 0 ? void 0 : _b.Value;
                this.dates.set(dateValue, await DateService.formatDate(new Date((_d = (_c = document.Fields) === null || _c === void 0 ? void 0 : _c.Date) === null || _d === void 0 ? void 0 : _d.Value), 'dateFull'));
            });
        });
        (_a = this.listToHide) === null || _a === void 0 ? void 0 : _a.setAttribute('hidden', '');
        this.isLoading = false;
    }
    handleChange() {
        var _a, _b, _c;
        this.searchTerm = (_b = (_a = this.inputElement) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim();
        this.changeEvent.emit(this.searchTerm);
        this.pageTracker = this.searchFilters.map(() => 1);
        this.searchResults = [];
        if (!this.searchTerm) {
            (_c = this.listToHide) === null || _c === void 0 ? void 0 : _c.removeAttribute('hidden');
            return;
        }
        this.querySearch();
    }
    handleClearInput() {
        var _a;
        this.inputElement.value = '';
        this.searchTerm = '';
        this.changeEvent.emit(this.searchTerm);
        this.inputElement.focus();
        this.searchResults = [];
        (_a = this.listToHide) === null || _a === void 0 ? void 0 : _a.removeAttribute('hidden');
    }
    get element() { return this; }
    static get style() { return RocheListSearchStyle0; }
}, [1, "roche-list-search", {
        "filters": [1],
        "inputValue": [8, "input-value"],
        "disableBuiltInCludoSearch": [4, "disable-built-in-cludo-search"],
        "searchTerm": [32],
        "searchResults": [32],
        "isLoading": [32],
        "resultsPerPage": [32],
        "pageTracker": [32],
        "translations": [32],
        "dates": [32]
    }, [[0, "rochePaginationPageChange", "triggerNewPage"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-list-search", "roche-button", "roche-empty-state", "roche-icon", "roche-image", "roche-loading-spinner", "roche-pagination", "roche-teaser", "roche-video-player"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-list-search":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheListSearch$1);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "roche-empty-state":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "roche-image":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "roche-loading-spinner":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-pagination":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-teaser":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-video-player":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheListSearch = RocheListSearch$1;
const defineCustomElement = defineCustomElement$1;

export { RocheListSearch, defineCustomElement };

//# sourceMappingURL=roche-list-search.js.map